import { APIOutput } from "@amzn/ask-legal-domain";

/**
 * Options for executeWithRetry
 */
export interface ExecuteWithRetryOptions {
    /** Logging prefix for console messages (e.g., "[DragSave]") */
    logPrefix: string;
    /** Maximum number of retries on 409 conflict (default: 3).
     *  Single-user race conditions (e.g., drag + edit overlap) typically
     *  resolve within 1-2 retries. Only truly concurrent multi-user edits
     *  exhaust all retries and surface an error to the user. */
    maxRetries?: number;
    /** Delay in ms before each retry (default: 200) */
    backoffMs?: number;
    /** The API call to execute on each attempt. Should return parsed APIOutput. */
    execute: () => Promise<APIOutput<any>>;
    /** Called with parsed data on successful save. */
    onSuccess: (data: any) => void;
    /** Called when version conflict retries are exhausted (e.g., rollback UI). */
    onConflictExhausted?: () => void;
    /** Called on non-retryable API errors (e.g., rollback UI). */
    onNonRetryableError?: (err: any) => void;
    /** Alert message shown to user when retries exhausted. */
    conflictAlertMessage: string;
    /** Called before each retry to reload latest version from server. */
    onBeforeRetry?: () => Promise<any>;
    /** Called when operation succeeds (for save status tracking). */
    onComplete?: () => void;
    /** Called when operation fails (for save status tracking). */
    onFailed?: () => void;
}

/**
 * Reusable helper for API operations with version conflict retry logic.
 *
 * Retry behavior:
 * - On 409 (version conflict): reload latest version, backoff, retry up to maxRetries
 * - On any other error (network, non-409 API error, thrown exception): fail immediately
 * - Guarantees: exactly one of onComplete or onFailed is called
 */
export async function executeWithRetry(options: ExecuteWithRetryOptions): Promise<void> {
    const {
        logPrefix,
        maxRetries = 3,
        backoffMs = 200,
        execute,
        onSuccess,
        onConflictExhausted,
        onNonRetryableError,
        conflictAlertMessage,
        onBeforeRetry,
        onComplete,
        onFailed
    } = options;

    let retryCount = 0;

    while (true) {
        try {
            const output = await execute();

            if (output.isOk()) {
                onSuccess(output.data);
                onComplete?.();
                return;
            }

            if (output.err?.code === 409 && retryCount < maxRetries) {
                retryCount++;
                console.warn(`${logPrefix} Version conflict (retry ${retryCount}/${maxRetries})`);
                try {
                    await onBeforeRetry?.();
                } catch (reloadError) {
                    console.warn(`${logPrefix} onBeforeRetry failed, retrying with current version:`, reloadError);
                }
                await new Promise(resolve => setTimeout(resolve, backoffMs));
                continue; // Retry
            }

            // Non-retryable error or retries exhausted
            if (output.err?.code === 409) {
                console.error(`${logPrefix} Max retries (${maxRetries}) reached`);
                onConflictExhausted?.();
                alert(conflictAlertMessage);
            } else {
                console.error(`${logPrefix} Failed:`, output.err);
                onNonRetryableError?.(output.err);
            }

            onFailed?.();
            return;

        } catch (error) {
            // Network error or unexpected exception — fail immediately, no retry
            console.error(`${logPrefix} Unexpected error:`, error);
            onNonRetryableError?.(error);
            onFailed?.();
            return;
        }
    }
}
